import type { UrlObject } from "node:url";
import {
	type ComponentType,
	createContext,
	type ReactNode,
	useContext,
} from "react";

/**
 * リンク先の指定。next/link の `LinkProps["href"]` と構造的に同一の型で、
 * karte-ui が next に直接依存しないために再定義している。
 */
export type KarteUiHref = string | UrlObject;

export type KarteUiLinkProps = {
	href: KarteUiHref;
	children: ReactNode;
	"aria-label"?: string;
	"aria-current"?: "page";
};

/**
 * karte-ui のリンク描画に使うコンポーネント。アプリ側が next/link を注入する。
 *
 * karte-ui 自身が next/link を import すると、karte-ui とアプリで別々の next
 * インスタンスが読み込まれた場合に next/link が RouterContext を見つけられず、
 * 動的ルートの補間（`/shares/[shareId]` → `/shares/xxx`）が行われなくなる。
 */
export type KarteUiLinkComponent = ComponentType<KarteUiLinkProps>;

const LinkContext = createContext<KarteUiLinkComponent | null>(null);

export const KarteUiProvider = ({
	link,
	children,
}: {
	link: KarteUiLinkComponent;
	children: ReactNode;
}) => <LinkContext.Provider value={link}>{children}</LinkContext.Provider>;

export const useKarteUiLink = (): KarteUiLinkComponent => {
	const link = useContext(LinkContext);
	if (!link) {
		throw new Error(
			"karte-ui のリンクコンポーネントが未設定です。アプリのルートで <KarteUiProvider link={NextLink}> を設定してください。",
		);
	}
	return link;
};
